import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Prospecting from "./pages/Prospecting";
import Config from "./pages/Config";
import Dashboard from "./pages/Dashboard";
import Log from "./pages/Log";
import GoogleMapsPlaybook from "./pages/GoogleMapsPlaybook";
import Library from "./pages/Library";
import Integrations from "./pages/Integrations";
import FreeSources from "./pages/FreeSources";
import Kanban from "./pages/Kanban";
import GovKanban from "./pages/GovKanban";
import Webhooks from "./pages/Webhooks";
import Campaigns from "./pages/Campaigns";
import Insights from "./pages/Insights";
import Account from "./pages/Account";
import Tutorials from "./pages/Tutorials";
import FloatingHelpButton from "./components/tutorials/FloatingHelpButton";
import AppMarketplace from "./pages/AppMarketplace";
import Billing from "./pages/Billing";
import PublicWorks from "./pages/PublicWorks";
import GCLeaderboard from "./pages/GCLeaderboard";
import ProductCatalog from "./pages/ProductCatalog";
import LeadsFinder from "./pages/LeadsFinder";
import LeadsMap from "./pages/LeadsMap";
import B2CIntent from "./pages/B2CIntent";
import B2CLeadRadar from "./pages/b2c-radar/B2CLeadRadar";
import LeadGapIntelligence from "./pages/LeadGapIntelligence";
import InstagramScraper from "./pages/InstagramScraper";
import LinkedInScraper from "./pages/LinkedInScraper";
import YouTubeScraper from "./pages/YouTubeScraper";
import Replies from "./pages/Replies";
import ROI from "./pages/ROI";
import VerticalPage from "./pages/VerticalPage";
import TenantLanding from "./pages/TenantLanding";
import TenantAnalytics from "./pages/TenantAnalytics";
import ImpersonationBridge from "./pages/ImpersonationBridge";
import AuthCallback from "./pages/AuthCallback";
import MarketingAPIs from "./pages/MarketingAPIs";
import TrialExpired from "./pages/TrialExpired";
import { UpgradeModal } from "./components/TrialLock";
import GlobalTrialBanner from "./components/GlobalTrialBanner";
import OnboardingWizard from "./components/OnboardingWizard";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import { AboutPage, ContactPage, PrivacyPage, TermsPage, CookiesPage, CareersPage } from "./pages/PublicPages";
import ChangePassword from "./pages/ChangePassword";
import MasterDashboard from "./pages/master/MasterDashboard";
import MasterTenants from "./pages/master/MasterTenants";
import MasterDemoAccounts from "./pages/master/MasterDemoAccounts";
import MagicLogin from "./pages/MagicLogin";
import MasterPackages from "./pages/master/MasterPackages";
import MasterAgents from "./pages/master/MasterAgents";
import MasterAccessLogs from "./pages/master/MasterAccessLogs";
import MasterAnalytics from "./pages/master/MasterAnalytics";
import MasterEmailAnalytics from "./pages/master/MasterEmailAnalytics";
import ProcurementPulse from "./pages/master/ProcurementPulse";
import MasterContent from "./pages/master/MasterContent";
import MasterGmail from "./pages/master/MasterGmail";
import MasterClaireKB from "./pages/master/MasterClaireKB";
import MasterClaireInsights from "./pages/master/MasterClaireInsights";
import MasterTemplates from "./pages/master/MasterTemplates";
import MasterSectorCleanup from "./pages/master/MasterSectorCleanup";
import SmithDashboard from "./pages/smith/SmithDashboard";
import SmithProspects from "./pages/smith/SmithProspects";
import SmithProspectDetail from "./pages/smith/SmithProspectDetail";
import SmithRepository from "./pages/smith/SmithRepository";
import SmithHQ from "./pages/smith/SmithHQ";
import SmithAsk from "./pages/smith/SmithAsk";
import SmithICPs from "./pages/smith/SmithICPs";
import SmithMap from "./pages/smith/SmithMap";
import SmithKanban from "./pages/smith/SmithKanban";
import SmithVehicleSecurity from "./pages/smith/SmithVehicleSecurity";
import PrivateVehicleSecurityLanding from "./pages/PrivateVehicleSecurityLanding";
import SecurityAssessment from "./pages/SecurityAssessment";
import SmithImports from "./pages/smith/SmithImports";
// SmithContacts unified into Outreach AI — see /smith/outreach?tab=contacts
import SmithOutreach from "./pages/smith/SmithOutreach";
import SmithBlacklist from "./pages/smith/SmithBlacklist";
import SmithSequences from "./pages/smith/SmithSequences";
import SmithWatchlist from "./pages/smith/SmithWatchlist";
import SmithOpportunityHub from "./pages/smith/SmithOpportunityHub";
import SmithAISearch from "./pages/smith/SmithAISearch";
import SmithAISearchCompany from "./pages/smith/SmithAISearchCompany";
import SmithAISearchSignals from "./pages/smith/SmithAISearchSignals";
import SmithAISearchSettings from "./pages/smith/SmithAISearchSettings";
import Subscription from "./pages/Subscription";
import { LangProvider } from "./i18n/LangContext";
import { ThemeProvider } from "./theme/ThemeContext";
import { TourProvider } from "./tour/TourContext";
import { AuthProvider } from "./auth/AuthContext";
import { SidebarProvider } from "./components/SidebarContext";
import ProtectedRoute from "./auth/ProtectedRoute";
import ClaireWidget from "./components/ClaireWidget";
import ImpersonationBanner from "./components/ImpersonationBanner";
import BackToMenu from "./components/BackToMenu";
import CreditsGuard from "./components/CreditsGuard";
import BillingHistory from "./pages/BillingHistory";
import CommandPalette from "./components/CommandPalette";
import CookieBanner from "./components/CookieBanner";
import MobileSidebarDrawer from "./components/MobileSidebarDrawer";
import NotFound from "./pages/NotFound";
import GeoLanding from "./pages/GeoLanding";
import CitiesIndex from "./pages/CitiesIndex";
import Sunbiz from "./pages/Sunbiz";
import MultiState from "./pages/MultiState";
import NoWebsiteLeads from "./pages/NoWebsiteLeads";
import YelpFinder from "./pages/YelpFinder";
import AutoCruise from "./pages/AutoCruise";
import { useParams } from "react-router-dom";

// Iter100 — React Router v7 requires `:param` to match a WHOLE segment.
// `/leads-:slug` is illegal grammar, so we accept any single-segment path
// here and dispatch based on the `leads-` prefix. Defined routes above this
// component (e.g. /login, /dashboard) win automatically because exact-literal
// matches outrank parametric ones.
function PublicSlugDispatcher() {
  const { publicSlug } = useParams();
  const slug = publicSlug || "";
  if (slug.startsWith("leads-")) return <GeoLanding key={slug} forcedSlug={slug} />;
  return <NotFound />;
}
import Forced2FAGate from "./components/Forced2FAGate";
import {
  BuscaDeLeadsB2B, GerenciamentoDeLeads, LeadScoring, IdentificacaoPorIP,
  CrmParaProspeccao, PoliticaDePrivacidade, LGPD, Seguranca,
} from "./pages/SeoLandings";

function App() {
  return (
    <ThemeProvider>
      <LangProvider>
        <AuthProvider>
          <SidebarProvider>
          <div className="App min-h-screen bg-[var(--al-bg)] text-[var(--al-text)]">
            <BrowserRouter>
              <ImpersonationBanner />
              <BackToMenu />
              <CreditsGuard />
              <CommandPalette />
              <CookieBanner />
              <MobileSidebarDrawer />
              <GlobalTrialBanner />
              <OnboardingWizard />
              <FloatingHelpButton />
              {/* Forced2FAGate disabled by user request (Iter85) — keep import
                  so the file stays compileable; gate remains opt-in via /account. */}
              {false && <Forced2FAGate />}
              <TourProvider>
                <Routes>
                  {/* Public */}
                  <Route path="/" element={<Landing />} />
                  <Route path="/landing" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/impersonate" element={<ImpersonationBridge />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/signup" element={<Login />} />
                  <Route path="/magic-login" element={<MagicLogin />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/cookies" element={<CookiesPage />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />

                  {/* SEO-optimised public landings (PT-BR) — Iter92.
                      Each renders rich semantic HTML + dynamic meta tags +
                      JSON-LD for crawlers and AI assistants. */}
                  <Route path="/busca-de-leads-b2b" element={<BuscaDeLeadsB2B />} />
                  <Route path="/gerenciamento-de-leads" element={<GerenciamentoDeLeads />} />
                  <Route path="/lead-scoring" element={<LeadScoring />} />
                  <Route path="/identificacao-de-empresas-por-ip" element={<IdentificacaoPorIP />} />
                  <Route path="/crm-para-prospeccao" element={<CrmParaProspeccao />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaDePrivacidade />} />
                  <Route path="/lgpd" element={<LGPD />} />
                  <Route path="/seguranca" element={<Seguranca />} />

                  {/* Iter218 · Smith SecureVehicle™ public surfaces */}
                  <Route path="/private-vehicle-security" element={<PrivateVehicleSecurityLanding />} />
                  <Route path="/security-assessment" element={<SecurityAssessment />} />

                  {/* Forced first-login password change (auth required, but bypasses ProtectedRoute redirect loop). */}
                  <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

                  {/* Master / Super Admin only */}
                  <Route path="/master"          element={<ProtectedRoute requireRole="super_admin"><MasterDashboard /></ProtectedRoute>} />
                  <Route path="/master/tenants"  element={<ProtectedRoute requireRole="super_admin"><MasterTenants /></ProtectedRoute>} />
                  <Route path="/master/demo-accounts" element={<ProtectedRoute requireRole="super_admin"><MasterDemoAccounts /></ProtectedRoute>} />
                  <Route path="/master/packages" element={<ProtectedRoute requireRole="super_admin"><MasterPackages /></ProtectedRoute>} />
                  <Route path="/master/agents"   element={<ProtectedRoute requireRole="super_admin"><MasterAgents /></ProtectedRoute>} />
                  <Route path="/master/access-logs" element={<ProtectedRoute requireRole="super_admin"><MasterAccessLogs /></ProtectedRoute>} />
                  <Route path="/master/analytics"   element={<ProtectedRoute requireRole="super_admin"><MasterAnalytics /></ProtectedRoute>} />
                  <Route path="/master/email-analytics" element={<ProtectedRoute requireRole="super_admin"><MasterEmailAnalytics /></ProtectedRoute>} />
                  <Route path="/master/procurement-pulse" element={<ProtectedRoute requireRole="super_admin"><ProcurementPulse /></ProtectedRoute>} />
                  <Route path="/master/content"     element={<ProtectedRoute requireRole="super_admin"><MasterContent /></ProtectedRoute>} />
                  <Route path="/master/gmail"       element={<ProtectedRoute requireRole="super_admin"><MasterGmail /></ProtectedRoute>} />
                  <Route path="/master/claire-kb"   element={<ProtectedRoute requireRole="super_admin"><MasterClaireKB /></ProtectedRoute>} />
                  <Route path="/master/sectors"    element={<ProtectedRoute requireRole="super_admin"><MasterSectorCleanup /></ProtectedRoute>} />
                  <Route path="/master/claire-insights" element={<ProtectedRoute requireRole="super_admin"><MasterClaireInsights /></ProtectedRoute>} />
                  <Route path="/master/templates"   element={<ProtectedRoute requireRole="super_admin"><MasterTemplates /></ProtectedRoute>} />

                  {/* Smith Protective — Master-accessible tenant module (Iter159-160) */}
                  <Route path="/smith"                    element={<ProtectedRoute requireRole="super_admin"><SmithDashboard /></ProtectedRoute>} />
                  <Route path="/smith/prospects"          element={<ProtectedRoute requireRole="super_admin"><SmithProspects /></ProtectedRoute>} />
                  <Route path="/smith/repository"         element={<ProtectedRoute requireRole="super_admin"><SmithRepository /></ProtectedRoute>} />
                  <Route path="/smith/hq"                 element={<ProtectedRoute requireRole="super_admin"><SmithHQ /></ProtectedRoute>} />
                  <Route path="/smith/ask"                element={<ProtectedRoute requireRole="super_admin"><SmithAsk /></ProtectedRoute>} />
                  <Route path="/smith/prospects/:id"      element={<ProtectedRoute requireRole="super_admin"><SmithProspectDetail /></ProtectedRoute>} />
                  <Route path="/smith/icps"               element={<ProtectedRoute requireRole="super_admin"><SmithICPs /></ProtectedRoute>} />
                  <Route path="/smith/map"                element={<ProtectedRoute requireRole="super_admin"><SmithMap /></ProtectedRoute>} />
                  <Route path="/smith/kanban"             element={<ProtectedRoute requireRole="super_admin"><SmithKanban /></ProtectedRoute>} />
                  <Route path="/smith/vehicle-security"    element={<ProtectedRoute requireRole="super_admin"><SmithVehicleSecurity /></ProtectedRoute>} />
                  <Route path="/smith/imports"            element={<ProtectedRoute requireRole="super_admin"><SmithImports /></ProtectedRoute>} />
                  {/* Iter199 — legacy /smith/contacts unified into Outreach AI */}
                  <Route path="/smith/contacts"           element={<Navigate to="/smith/outreach?tab=contacts" replace />} />
                  <Route path="/smith/outreach"           element={<ProtectedRoute requireRole="super_admin"><SmithOutreach /></ProtectedRoute>} />
                  <Route path="/smith/blacklist"          element={<ProtectedRoute requireRole="super_admin"><SmithBlacklist /></ProtectedRoute>} />
                  <Route path="/smith/sequences"          element={<ProtectedRoute requireRole="super_admin"><SmithSequences /></ProtectedRoute>} />
                  <Route path="/smith/watchlist"          element={<ProtectedRoute requireRole="super_admin"><SmithWatchlist /></ProtectedRoute>} />
                  <Route path="/smith/opportunity-hub"    element={<ProtectedRoute requireRole="super_admin"><SmithOpportunityHub /></ProtectedRoute>} />
                  <Route path="/smith/ai-search"          element={<ProtectedRoute requireRole="super_admin"><SmithAISearch /></ProtectedRoute>} />
                  <Route path="/smith/ai-search/signals"  element={<ProtectedRoute requireRole="super_admin"><SmithAISearchSignals /></ProtectedRoute>} />
                  <Route path="/smith/ai-search/settings" element={<ProtectedRoute requireRole="super_admin"><SmithAISearchSettings /></ProtectedRoute>} />
                  <Route path="/smith/ai-search/company/:id" element={<ProtectedRoute requireRole="super_admin"><SmithAISearchCompany /></ProtectedRoute>} />

                  {/* Protected app */}
                  <Route path="/prospecting" element={<ProtectedRoute><Prospecting /></ProtectedRoute>} />
                  <Route path="/tenant-analytics" element={<ProtectedRoute><TenantAnalytics /></ProtectedRoute>} />
                  <Route path="/search" element={<Navigate to="/prospecting" replace />} />
                  <Route path="/config" element={<ProtectedRoute><Config /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/integrations" element={<ProtectedRoute><Integrations /></ProtectedRoute>} />
                  <Route path="/settings/sources" element={<ProtectedRoute><FreeSources /></ProtectedRoute>} />
                  <Route path="/free-sources" element={<Navigate to="/settings/sources" replace />} />
                  <Route path="/kanban" element={<ProtectedRoute><Kanban /></ProtectedRoute>} />
                  <Route path="/gov-kanban" element={<ProtectedRoute><GovKanban /></ProtectedRoute>} />
                  <Route path="/webhooks" element={<ProtectedRoute><Webhooks /></ProtectedRoute>} />
                  <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
                  <Route path="/insights" element={<ProtectedRoute><Insights /></ProtectedRoute>} />
                  <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                  {/* /products was removed — its tools now live inside Kanban → 'Tools'.
                      Redirect any deep-linked bookmark to /kanban so nothing breaks. */}
                  <Route path="/products" element={<Navigate to="/kanban" replace />} />
                  <Route path="/marketplace" element={<ProtectedRoute><AppMarketplace /></ProtectedRoute>} />
                  <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                  <Route path="/billing/history" element={<ProtectedRoute><BillingHistory /></ProtectedRoute>} />
                  <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
                  <Route path="/public-works" element={<ProtectedRoute><PublicWorks /></ProtectedRoute>} />
                  <Route path="/gc-leaderboard" element={<ProtectedRoute><GCLeaderboard /></ProtectedRoute>} />
                  <Route path="/product-catalog" element={<ProtectedRoute><ProductCatalog /></ProtectedRoute>} />
                  <Route path="/leads-finder" element={<ProtectedRoute><LeadsFinder /></ProtectedRoute>} />
                  <Route path="/leads-map" element={<ProtectedRoute><LeadsMap /></ProtectedRoute>} />
                  <Route path="/b2c-intent" element={<ProtectedRoute><B2CIntent /></ProtectedRoute>} />
                  <Route path="/b2c-radar" element={<ProtectedRoute><B2CLeadRadar /></ProtectedRoute>} />
                  <Route path="/lead-gap" element={<ProtectedRoute><LeadGapIntelligence /></ProtectedRoute>} />
                  <Route path="/instagram" element={<ProtectedRoute><InstagramScraper /></ProtectedRoute>} />
                  <Route path="/linkedin" element={<ProtectedRoute><LinkedInScraper /></ProtectedRoute>} />
                  <Route path="/youtube" element={<ProtectedRoute><YouTubeScraper /></ProtectedRoute>} />
                  <Route path="/replies" element={<ProtectedRoute><Replies /></ProtectedRoute>} />
                  <Route path="/roi" element={<ProtectedRoute><ROI /></ProtectedRoute>} />
                  <Route path="/v/:key" element={<VerticalPage />} />
                  <Route path="/p/:slug" element={<TenantLanding />} />
                  <Route path="/marketing-apis" element={<ProtectedRoute><MarketingAPIs /></ProtectedRoute>} />
                  <Route path="/trial-expired" element={<ProtectedRoute><TrialExpired /></ProtectedRoute>} />
                  <Route path="/log" element={<ProtectedRoute><Log /></ProtectedRoute>} />
                  <Route path="/playbook/google-maps" element={<ProtectedRoute><GoogleMapsPlaybook /></ProtectedRoute>} />
                  <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
                  <Route path="/tutorials" element={<ProtectedRoute><Tutorials /></ProtectedRoute>} />
                  <Route path="/sunbiz" element={<ProtectedRoute requireRole="super_admin"><Sunbiz /></ProtectedRoute>} />
                  <Route path="/multi-state" element={<ProtectedRoute requireRole="super_admin"><MultiState /></ProtectedRoute>} />
                  <Route path="/no-website-leads" element={<ProtectedRoute requireRole="super_admin"><NoWebsiteLeads /></ProtectedRoute>} />
                  <Route path="/yelp" element={<ProtectedRoute requireRole="super_admin"><YelpFinder /></ProtectedRoute>} />
                  <Route path="/auto-cruise" element={<ProtectedRoute requireRole="super_admin"><AutoCruise /></ProtectedRoute>} />
                  {/* Iter100 — SEO geo-landing pages: /leads-orlando-fl, /leads-houston-tx, … */}
                  <Route path="/:publicSlug" element={<PublicSlugDispatcher />} />
                  <Route path="/cities" element={<CitiesIndex />} />
                  <Route path="/404" element={<NotFound />} />
                  {/* Catch-all: render the branded 404 page for any unmatched route. */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </TourProvider>
              {/* Iter104 — Claire in-app AI assistant (logged-in users).
                  The widget itself decides whether to render based on
                  auth + mode prop. */}
              <ClaireWidget />
              {/* Iter106 — Claire in SALES mode for anonymous visitors on
                  public routes. Renders only when no user is logged in. */}
              <ClaireWidget mode="sales" />
              <UpgradeModal />
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
          </div>
          </SidebarProvider>
        </AuthProvider>
      </LangProvider>
    </ThemeProvider>
  );
}

export default App;
